import FUNDO_SITUACAO from '../../../enums/FUNDO_SITUACAO';

export const COMPARADOR_FUNDOS_SEARCH_PAGE_SIZE = 40;

/**
 * Formata a pesquisa livre no contrato praticado pelo serviço de ativos.
 *
 * @param {string} pValue Termo informado no diálogo.
 * @returns {string} Termo normalizado para a API.
 */
export function formatComparadorFundosSearch(pValue = '') {
  return String(pValue)
    .trim()
    .replace(/[./-]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map(pTerm => `+${pTerm}*`)
    .join('');
}

/**
 * Converte a resposta pública para o modelo mínimo usado pelo diálogo.
 *
 * @param {Object|Object[]} pResponse Resposta de pesquisas.ativo.
 * @param {string} pQuery Consulta original para priorização visual.
 * @returns {Object[]} Resultados normalizados.
 */
export function normalizeComparadorFundosSearchResults(pResponse, pQuery = '') {
  const xResponseData = pResponse?.data?.data || pResponse?.data || pResponse || [];
  const xItems = Array.isArray(xResponseData) ? xResponseData : Object.values(xResponseData);
  const xTicker = String(pQuery).trim().toUpperCase();

  return xItems
    .map(pItem => {
      const xId = String(pItem?.ifeed_ativo_id || pItem?.ativo_id || '').trim();
      const xGestor = pItem?.gestor || '';
      const xEmissor = pItem?.emissor || '';

      return {
        id: xId,
        ticker: pItem?.ticker || pItem?.codigo || '',
        ativo_seuid: pItem?.ativo_seuid || '',
        ativo: pItem?.ativo || '',
        cnpj: pItem?.cnpj || pItem?.emissor_cnpj || pItem?.emissor?.cnpj || '',
        gestor:
          typeof xGestor === 'object' ? xGestor?.pessoa_fantasia || xGestor?.pessoa || '' : xGestor,
        emissor:
          typeof xEmissor === 'object'
            ? xEmissor?.pessoa_fantasia || xEmissor?.pessoa || ''
            : xEmissor,
        ativoTipoId: pItem?.ativo_tipo_id || null,
        ativoTipo: pItem?.ativo_tipo || null,
        logoPessoaId: pItem?.logo_pessoa_id || pItem?.emissor?.logo_pessoa_id || null,
        logoOk: pItem?.emissor?.logo_ok || null,
        pessoa: pItem?.emissor?.pessoa || null,
        classe: pItem?.classe || null,
        mercado: pItem?.mercado || null,
        vencimento: pItem?.vencimento || null,
        isin: pItem?.isin || null,
        serie: pItem?.serie || null,
        esg: pItem.esg,
        benchmarkIndicadorId: pItem?.benchmark_indicador_id || null,
        exclusivo: pItem?.exclusivo,
        fechado: pItem?.fechado,
        fundoEstrategiaId: pItem?.fundo_estrategia_id || null,
        raw: pItem
      };
    })
    .filter(pItem => Boolean(pItem.id))
    .sort((pLeft, pRight) => {
      const xLeftExact = pLeft.ticker.toUpperCase() === xTicker;
      const xRightExact = pRight.ticker.toUpperCase() === xTicker;

      return Number(xRightExact) - Number(xLeftExact);
    });
}

/**
 * Monta os parâmetros comuns da lista, pesquisa e filtros de fundos.
 *
 * @param {Object} pValues Estado local do diálogo.
 * @returns {Object} Parâmetros aceitos por pesquisas.ativo.
 */
export function buildComparadorFundosSearchParams(pValues = {}) {
  const xQuery = String(pValues.query || '').trim();
  const xSort = String(pValues.sort || '').trim();
  const pvHasValue = pValue => pValue !== '' && pValue !== null && pValue !== undefined;
  const pvBooleanFilter = pValue =>
    pValue === 'true' ? true : pValue === 'false' ? false : pValue;
  const pvNumberFilter = pValue => {
    const xValue = Number(pValue);

    return Number.isFinite(xValue) ? xValue : undefined;
  };
  const pvCsvFilter = pValue => {
    if (Array.isArray(pValue)) {
      return pValue.length ? pValue.join(',') : undefined;
    }

    return pvHasValue(pValue) ? String(pValue) : undefined;
  };
  const xSituacao = String(pvCsvFilter(pValues.situacao) || '').trim();
  const xParams = {
    mercado: 'FND',
    page: pValues.page || 1,
    size: pValues.size || COMPARADOR_FUNDOS_SEARCH_PAGE_SIZE,
    situacao: xSituacao || FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value,
    sort: xSort
      ? xSort.includes('relevancia DESC')
        ? xSort
        : `${xSort}, relevancia DESC`
      : xQuery
      ? 'relevancia DESC, __mr__ DESC'
      : 'relevancia DESC',
    ...(xQuery.length >= 2 ? { pesquisa: formatComparadorFundosSearch(xQuery) } : {}),
    ...(pvHasValue(pValues.top_setor_id) ? { top_setor_id: pValues.top_setor_id } : {})
  };

  [
    'ativo_tipo_id',
    'benchmark_indicador_id',
    'fundo_estrategia_id',
    'publico_alvo',
    'distribuicao'
  ].forEach(pName => {
    const xValue = pvCsvFilter(pValues[pName]);

    if (typeof xValue !== 'undefined') {
      xParams[pName] = xValue;
    }
  });

  [
    'cvm_175',
    'exclusivo',
    'fechado',
    'longo',
    'cotas',
    'nao_padronizado',
    'condominio_fechado',
    'responsabilidade_limitada',
    'qualificado',
    'profissional',
    'previdenciario',
    'estruturado',
    'esg'
  ].forEach(pName => {
    if (pvHasValue(pValues[pName])) {
      xParams[pName] = pvBooleanFilter(pValues[pName]);
    }
  });

  [
    'criado_a_mais_de',
    'taxa_adm',
    'aplicacao_min',
    'resgate_carencia',
    'amostragem',
    'retorno',
    'perc_dias_acima_benchmark',
    'perc_meses_acima_benchmark',
    'sharpe',
    'tracking_error',
    'volatilidade',
    'pl',
    'cotistas',
    'gestor_pl',
    'gestor_cotistas',
    'adm_pl',
    'adm_cotistas'
  ].forEach(pName => {
    if (pvHasValue(pValues[pName])) {
      const xValue = pvNumberFilter(pValues[pName]);

      if (typeof xValue !== 'undefined') {
        xParams[pName] = xValue;
      }
    }
  });

  return xParams;
}
